import { IconButton } from '../HBIconButton/HBIconButton.styles';
import React, { ComponentProps, forwardRef } from 'react';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import HBTooltip, { ITooltipProps } from '../HBTooltip/HBTooltip';
import { IconProps } from '@honeybook/icons';
import { MustIncludeOneOfBasicTestIdProps } from '../common/internal/test-id.types';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import { ButtonLoader } from '../HBButton/HBButton.styles';
import { TLoaderSize } from '../HBLoader/HBLoader.styles';
import { vars } from '../../theme';
import { CSSDimensionValue } from '../../tokens/types';

type Theme = 'neutral' | 'subtle' | 'accent';
type Solid = 'solid';
type Invisible = 'invisible';
type Wrapper = Solid | 'floating' | Invisible;
export type TIconButtonTheme =
    | `${Theme}-${Wrapper}`
    | `error-${Solid}`
    | `light-${Invisible}`;

export type TIconButtonSize = 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const iconButtonSizeToLoaderSize: Record<TIconButtonSize, TLoaderSize> = {
    xxs: 'xxs',
    xs: 'xs',
    sm: 'xs',
    md: 'xs',
    lg: 'xs',
    xl: 'small'
};

const iconButtonThemeToLoaderColor: Record<TIconButtonTheme, string> = {
    'neutral-floating': vars.colors.text.primary,
    'neutral-invisible': vars.colors.text.primary,
    'neutral-solid': vars.colors.text.primary,
    'subtle-floating': vars.colors.text.primary,
    'subtle-invisible': vars.colors.text.primary,
    'subtle-solid': vars.colors.text.primary,
    'accent-floating': vars.colors.text.accent,
    'accent-invisible': vars.colors.text.accent,
    'accent-solid': vars.colors.text.invertedPrimary,
    'error-solid': vars.colors.text.danger,
    'light-invisible': vars.colors.text.invertedPrimary
};

interface BaseProps {
    /** Disables the button and blocks interaction. When combined with `loading`, also blocks interaction while the spinner is shown. */
    disabled?: boolean;
    /** HTML inert attribute; prevents keyboard focus and interaction without visually indicating disabled state. */
    inert?: boolean;
    /** Icon component function; must match the expected pixel size for the given `size` prop or a dev-mode error is thrown. */
    icon: (props: IconProps) => JSX.Element;
    /** When true, applies the selected/active visual state via a CSS class. */
    isSelected?: boolean;
    /** Accessible label rendered via VisuallyHidden for screen readers; not displayed visually. Always required. */
    label: string;
    /** Button dimensions and expected icon pixel size: xxs→12px, xs→16px, sm/md/lg→24px, xl→32px. A size/icon mismatch logs a dev-mode warning. Defaults to 'xs'. */
    size?: TIconButtonSize;
    /** Stable selector for automated tests. Use `testIdName` when integrating with the component test-id system. */
    testId?: string;
    /** Visual theme composed as '{color}-{wrapper}' (e.g. 'neutral-invisible', 'accent-solid'); also determines loader spinner color. Defaults to 'neutral-invisible'. */
    buttonTheme?: TIconButtonTheme;
    /** Content displayed in an HBTooltip on hover; tooltip is disabled when this is undefined. */
    tooltip?: string | JSX.Element;
    /** Partial overrides passed to the wrapping HBTooltip (e.g. placement, offset). */
    tooltipProps?: Partial<ITooltipProps>;
    /** CSS class applied to the root element. */
    className?: string;
    /** Shows a spinner and blocks interaction. Spinner color is determined by `buttonTheme`. */
    loading?: boolean;
    /** Renders the button as a custom HTML element via styled-components `as` prop; ignored when `href` is provided. */
    tag?: string;
    /** CSS margin shorthand applied directly to the button element. */
    margin?: CSSDimensionValue;
}

export interface IIconButtonProps extends BaseProps {
    /** HTML button type; defaults to 'button' to prevent accidental form submission. */
    type?: ComponentProps<'button'>['type'];
    /** Called when the button is clicked. */
    onClick?: ComponentProps<'button'>['onClick'];
    /** Called when a mouse button is pressed on the button element. */
    onMouseDown?: ComponentProps<'button'>['onMouseDown'];
}

export interface IIconLinkProps extends BaseProps {
    /** Renders the icon button as an anchor element linking to this URL. */
    href: string;
    /** When true, adds `target="_blank"` to open the link in a new browser tab. */
    isNewTab?: boolean;
    /** Called when the anchor element is clicked. */
    onClick?: ComponentProps<'a'>['onClick'];
    /** Called when a mouse button is pressed on the anchor element. */
    onMouseDown?: ComponentProps<'a'>['onMouseDown'];
}

const HBIconButton = forwardRef<
    HTMLButtonElement | HTMLAnchorElement,
    (IIconButtonProps | IIconLinkProps) & MustIncludeOneOfBasicTestIdProps
>(
    (
        {
            testIdName,
            testId,
            icon: Icon,
            isSelected,
            label,
            size = 'xs',
            buttonTheme = 'neutral-invisible',
            tooltip,
            tooltipProps,
            disabled,
            inert,
            loading,
            tag,
            className = '',
            margin,
            ...props
        },
        ref
    ) => {
        const isLink = 'href' in props;

        assertCorrectIconSizeInDev({ icon: Icon, size });

        const finalTestId = useBuildTestIdInternal({
            testId,
            identifier: testIdName,
            suffix: isLink ? 'link' : 'button',
            componentName: 'HBIconButton'
        });

        const _props = isLink
            ? {
                  ...props,
                  ...(props.isNewTab && { target: '_blank' }),
                  as: 'a' as Parameters<typeof IconButton>[0]['as']
              }
            : tag
            ? { ...props, as: tag }
            : {
                  ...props,
                  type: props.type || 'button'
              };

        const iconButtonClassName = `${className}${
            isSelected ? ' selected' : ''
        }`;

        const loaderSize = iconButtonSizeToLoaderSize[size];
        const loaderColor = iconButtonThemeToLoaderColor[buttonTheme];

        return (
            <HBTooltip
                disabled={!tooltip}
                placement="top"
                offsetY={6}
                {...tooltipProps}
                target={
                    <IconButton
                        inert={inert}
                        {..._props}
                        ref={ref}
                        disabled={disabled || loading}
                        className={iconButtonClassName}
                        data-testid={finalTestId}
                        size={size}
                        theme={buttonTheme}
                        $margin={margin}
                    >
                        {loading && (
                            <ButtonLoader
                                testId="icon-button--loader"
                                size={loaderSize}
                                color={loaderColor}
                            />
                        )}
                        <Icon
                            color={null /* to make sure no color is applied **/}
                        />
                        <VisuallyHidden.Root>{label}</VisuallyHidden.Root>
                    </IconButton>
                }
            >
                {tooltip}
            </HBTooltip>
        );
    }
);

export default HBIconButton;

function assertCorrectIconSizeInDev({ icon, size }: Partial<BaseProps>) {
    if (
        process.env.NODE_ENV === 'development' ||
        process.env.NODE_ENV === 'test'
    ) {
        switch (size) {
            case 'xxs':
                warnOnIncorrectSize(12);
                break;
            case 'xs':
                warnOnIncorrectSize(16);
                break;
            case 'xl':
                warnOnIncorrectSize(32);
                break;
            default:
                warnOnIncorrectSize(24);
        }

        function warnOnIncorrectSize(pixels: 12 | 16 | 24 | 32) {
            const digitsAtTheEnd = icon.name?.match(/\d+$/)?.[0];
            if (digitsAtTheEnd && digitsAtTheEnd !== String(pixels)) {
                // eslint-disable-next-line no-console -- dev-only log
                console.warn(
                    `HBIconButton: If the size is "${size}" the icon must be of size ${pixels}, received: ${icon.name}`
                );
            }
        }
    }
}
